import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import GmModal from 'app/shared/modal/modal';
import { MultiSelect } from 'app/shared/select';
import { useLeadActivityService } from 'hooks/api/agents/lead/activity';
import { useMeetingService } from 'hooks/api/agents/meeting';
import { useMessageService } from 'hooks/api/agents/message';
import styles from './timeline.module.css';

const CHANNEL_OPTIONS = [
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' }
];

const OUTCOMES = [
  { key: 'positive', label: 'Positive' },
  { key: 'neutral', label: 'Neutral' },
  { key: 'negative', label: 'Negative' }
];

const MEETING_STATUS = {
  scheduled: { label: 'Scheduled', cls: 'statusScheduled' },
  held: { label: 'Held', cls: 'statusHeld' },
  cancelled: { label: 'Cancelled', cls: 'statusCancelled' },
  no_show: { label: 'No show', cls: 'statusNoShow' }
};

const EMPTY_MEETING = {
  firstname: '',
  lastname: '',
  email: '',
  phone: '',
  time: '',
  title: '',
  link: ''
};

const toIso = (local) => {
  if (!local) return null;
  const date = new Date(local);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const toLocalInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const relativeTime = (value) => {
  if (!value) return '';
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  const abs = Math.abs(diff);
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const suffix = diff < 0 ? 'from now' : 'ago';

  if (abs < minute) return 'just now';
  if (abs < hour) return `${Math.round(abs / minute)}m ${suffix}`;
  if (abs < day) return `${Math.round(abs / hour)}h ${suffix}`;
  if (abs < 7 * day) return `${Math.round(abs / day)}d ${suffix}`;
  return new Date(value).toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const formatDateTime = (value) =>
  value
    ? new Date(value).toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : '';

const dayLabelFor = (value) => {
  if (!value) return 'Earlier';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Earlier';
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
};

const personName = (person = {}) =>
  [person.firstname || person.first_name, person.lastname || person.last_name]
    .filter(Boolean)
    .join(' ') ||
  person.email ||
  '';

const hostFromUser = (user = {}) => ({
  id: user.id != null ? String(user.id) : '',
  firstname: user.firstname || user.first_name || user.name || '',
  lastname: user.lastname || user.last_name || '',
  email: user.email || ''
});

export const SubjectTimeline = ({
  subject,
  host = null,
  defaultInvitee = null,
  contacts = [],
  composer_mode = null,
  onCloseComposer = () => {}
}) => {
  const user_data = useSelector((store) => store.user_data);
  const { createActivity, fetchActivities } = useLeadActivityService();
  const { createMeeting, fetchMeetings, updateMeeting } = useMeetingService();
  const { fetchMessages, sendMessage } = useMessageService();

  const [activities, setActivities] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(false);

  const [body, setBody] = useState('');
  const [outcome, setOutcome] = useState('neutral');
  const [meetingForm, setMeetingForm] = useState(EMPTY_MEETING);
  const [saving, setSaving] = useState(false);
  const [rescheduling_id, setReschedulingId] = useState(null);
  const [reschedule_time, setRescheduleTime] = useState('');

  const [gm_messages, setGmMessages] = useState([]);
  const [messages_loading, setMessagesLoading] = useState(false);
  const [selected_message_id, setSelectedMessageId] = useState(null);
  const [selected_contact_id, setSelectedContactId] = useState(null);
  const [channel, setChannel] = useState(null);
  const [sending_message, setSendingMessage] = useState(false);

  const subject_key = subject ? `${subject.kind}:${subject.id}` : null;
  const effective_host = host && host.email ? host : hostFromUser(user_data);

  useEffect(() => {
    if (!subject_key) return;
    const query = `subject.kind=${subject.kind}&subject.id=${subject.id}`;
    setLoading(true);
    Promise.all([
      fetchActivities({ query_string: `${query}&sort_by=-occurred_at&population=50` }),
      fetchMeetings({ query_string: `${query}&sort_by=-time&population=50` })
    ])
      .then(([{ activities: a }, { meetings: m }]) => {
        setActivities(a || []);
        setMeetings(m || []);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject_key]);

  useEffect(() => {
    if (defaultInvitee) {
      setMeetingForm((prev) => ({
        ...prev,
        firstname: defaultInvitee.firstname || '',
        lastname: defaultInvitee.lastname || '',
        email: defaultInvitee.email || '',
        phone: defaultInvitee.phone || ''
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultInvitee?.email]);

  useEffect(() => {
    if (composer_mode !== 'message') return;
    setSelectedMessageId(null);
    setChannel(null);
    setSelectedContactId(defaultInvitee?.id ?? null);
    if (gm_messages.length || messages_loading) return;
    setMessagesLoading(true);
    fetchMessages({ query_string: 'population=200' })
      .then(({ messages: fetched }) => setGmMessages(fetched || []))
      .finally(() => setMessagesLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composer_mode]);

  const items = useMemo(() => {
    const merged = [
      ...activities.map((a) => ({ type: 'activity', time: a.occurred_at, data: a })),
      ...meetings.map((m) => ({ type: 'meeting', time: m.time, data: m }))
    ].filter((item) => item.data && (item.data.id || item.data._id));

    merged.sort((x, y) => new Date(y.time).getTime() - new Date(x.time).getTime());

    let previous_label = '';
    return merged.map((item) => {
      const label = dayLabelFor(item.time);
      const show_label = label !== previous_label;
      previous_label = label;
      return { ...item, label, show_label };
    });
  }, [activities, meetings]);

  const setMeetingField = (field, value) => setMeetingForm((prev) => ({ ...prev, [field]: value }));

  const message_options = useMemo(
    () => gm_messages.map((m) => ({ value: m.id, label: m.title })),
    [gm_messages]
  );

  const contact_options = useMemo(
    () =>
      contacts.map((c) => {
        const id = c.id || c._id;
        const name = personName(c) || 'Unnamed contact';
        const detail = c.email || c.phone || '';
        return { value: id, label: detail ? `${name} · ${detail}` : name, contact: c };
      }),
    [contacts]
  );

  const selected_contact =
    contact_options.find((o) => o.value === selected_contact_id)?.contact || null;

  const channel_options = CHANNEL_OPTIONS.filter((o) =>
    o.value === 'email' ? !!selected_contact?.email : !!selected_contact?.phone
  );

  const handleContactChange = (option) => {
    setSelectedContactId(option?.value ?? null);
    setChannel(null);
  };

  const can_send_message =
    !sending_message &&
    !!selected_message_id &&
    !!channel &&
    !!selected_contact &&
    (channel === 'email' ? !!selected_contact.email : !!selected_contact.phone);

  const handleSendMessage = async () => {
    if (!can_send_message) return;
    setSendingMessage(true);
    const data = {
      recipient: {
        firstname: selected_contact.first_name || '',
        lastname: selected_contact.last_name || '',
        email: selected_contact.email || null,
        phone: selected_contact.phone || null
      },
      sender: effective_host,
      channel
    };
    const sent = await sendMessage(selected_message_id, { data });
    setSendingMessage(false);
    if (sent) {
      const sent_message = gm_messages.find((m) => m.id === selected_message_id);
      const channel_label = CHANNEL_OPTIONS.find((o) => o.value === channel)?.label || channel;
      const activity_data = {
        subject,
        outcome: 'neutral',
        body: `Sent "${sent_message?.title || 'message'}" via ${channel_label} to ${personName(
          selected_contact
        )}`
      };
      const { activity } = await createActivity(activity_data);
      if (activity) {
        const created = { occurred_at: new Date().toISOString(), ...activity_data, ...activity };
        setActivities((prev) => [created, ...prev]);
      }
      setSelectedMessageId(null);
      setChannel(null);
      onCloseComposer();
    }
  };

  const handleLogActivity = async () => {
    const trimmed = body.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    const data = { subject, outcome, body: trimmed };
    const { activity } = await createActivity(data);
    if (activity) {
      const created = { occurred_at: new Date().toISOString(), ...data, ...activity };
      setActivities((prev) => [created, ...prev]);
      setBody('');
      setOutcome('neutral');
      onCloseComposer();
    }
    setSaving(false);
  };

  const meeting_iso = toIso(meetingForm.time);
  const can_schedule =
    !saving && meetingForm.email.trim() !== '' && !!meeting_iso && !!effective_host.email;

  const handleScheduleMeeting = async () => {
    if (!can_schedule) return;
    setSaving(true);
    const data = {
      subject,
      invitee: {
        firstname: meetingForm.firstname.trim(),
        lastname: meetingForm.lastname.trim(),
        email: meetingForm.email.trim(),
        phone: meetingForm.phone.trim() || null
      },
      host: effective_host,
      time: meeting_iso,
      title: meetingForm.title.trim() || undefined,
      link: meetingForm.link.trim() || undefined
    };
    const { meeting } = await createMeeting(data);
    if (meeting) {
      const created = { status: 'scheduled', ...data, ...meeting };
      setMeetings((prev) => [created, ...prev]);
      setMeetingForm({
        ...EMPTY_MEETING,
        firstname: defaultInvitee?.firstname || '',
        lastname: defaultInvitee?.lastname || '',
        email: defaultInvitee?.email || '',
        phone: defaultInvitee?.phone || ''
      });
      onCloseComposer();
    }
    setSaving(false);
  };

  const applyMeetingUpdate = (id, patch) =>
    setMeetings((prev) => prev.map((m) => ((m.id || m._id) === id ? { ...m, ...patch } : m)));

  const handleStatusChange = async (meeting, status) => {
    const id = meeting.id || meeting._id;
    const previous = meeting.status;
    applyMeetingUpdate(id, { status });
    const { meeting: updated } = await updateMeeting(id, { status });
    if (!updated) applyMeetingUpdate(id, { status: previous });
  };

  const handleReschedule = async (meeting) => {
    const id = meeting.id || meeting._id;
    const iso = toIso(reschedule_time);
    if (!iso) return;
    setSaving(true);
    const { meeting: updated } = await updateMeeting(id, { time: iso });
    if (updated) applyMeetingUpdate(id, { time: iso });
    setSaving(false);
    setReschedulingId(null);
    setRescheduleTime('');
  };

  const renderActivity = (a) => {
    const outcome_key = a.outcome || 'neutral';
    const dot_cls =
      outcome_key === 'positive'
        ? styles.dotPositive
        : outcome_key === 'negative'
        ? styles.dotNegative
        : styles.dotNeutral;
    return (
      <div className={styles.entry}>
        <span className={`${styles.dot} ${dot_cls}`} />
        <div className={styles.entryBody}>
          {a.body && <span className={styles.entryText}>{a.body}</span>}
          <div className={styles.entryMeta}>
            {a.actor && <span className={styles.actor}>{a.actor}</span>}
            {a.occurred_at && <span className={styles.time}>{relativeTime(a.occurred_at)}</span>}
          </div>
        </div>
      </div>
    );
  };

  const renderMeeting = (m) => {
    const id = m.id || m._id;
    const status = m.status || 'scheduled';
    const status_meta = MEETING_STATUS[status] || MEETING_STATUS.scheduled;
    const invitee_name = personName(m.invitee);
    const is_scheduled = status === 'scheduled';
    return (
      <div className={`${styles.entry} ${styles.meetingEntry}`}>
        <span className={styles.meetingIcon}>📅</span>
        <div className={styles.entryBody}>
          <div className={styles.meetingTitleRow}>
            <span className={styles.meetingTitle}>{m.title || 'Meeting'}</span>
            <span className={`${styles.statusBadge} ${styles[status_meta.cls]}`}>
              {status_meta.label}
            </span>
          </div>
          <span className={styles.meetingWhen}>{formatDateTime(m.time)}</span>
          {invitee_name && (
            <span className={styles.meetingInvitee}>
              with {invitee_name}
              {m.invitee?.email ? ` · ${m.invitee.email}` : ''}
            </span>
          )}
          {!m.invitee?.email && (
            <span className={styles.meetingWarn}>No invitee email — reminder can&apos;t send</span>
          )}
          {m.link && (
            <a className={styles.joinLink} href={m.link} target="_blank" rel="noreferrer noopener">
              Join Meeting
            </a>
          )}

          {is_scheduled && rescheduling_id === id && (
            <div className={styles.rescheduleRow}>
              <input
                className={styles.input}
                type="datetime-local"
                value={reschedule_time}
                onChange={(e) => setRescheduleTime(e.target.value)}
              />
              <button
                type="button"
                className={styles.smallBtn}
                onClick={() => handleReschedule(m)}
                disabled={saving || !reschedule_time}
              >
                Save
              </button>
              <button
                type="button"
                className={styles.smallBtnGhost}
                onClick={() => setReschedulingId(null)}
              >
                Cancel
              </button>
            </div>
          )}

          {is_scheduled && rescheduling_id !== id && (
            <div className={styles.meetingActions}>
              <button
                type="button"
                className={styles.actionBtn}
                onClick={() => {
                  setReschedulingId(id);
                  setRescheduleTime(toLocalInput(m.time));
                }}
              >
                Reschedule
              </button>
              <button
                type="button"
                className={styles.actionBtn}
                onClick={() => handleStatusChange(m, 'held')}
              >
                Held
              </button>
              <button
                type="button"
                className={styles.actionBtn}
                onClick={() => handleStatusChange(m, 'no_show')}
              >
                No show
              </button>
              <button
                type="button"
                className={styles.actionBtnDanger}
                onClick={() => handleStatusChange(m, 'cancelled')}
              >
                Cancel
              </button>
            </div>
          )}
          {m.time && <span className={styles.time}>{relativeTime(m.time)}</span>}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.timeline}>
      <div className={styles.entries}>
        {loading && items.length === 0 && <p className={styles.empty}>Loading…</p>}
        {!loading && items.length === 0 && <p className={styles.empty}>No activity yet.</p>}
        {items.map((item) => (
          <React.Fragment key={`${item.type}-${item.data.id || item.data._id}`}>
            {item.show_label && <div className={styles.dayLabel}>{item.label}</div>}
            {item.type === 'activity' ? renderActivity(item.data) : renderMeeting(item.data)}
          </React.Fragment>
        ))}
      </div>

      <GmModal
        show_header
        show_title
        title={
          composer_mode === 'meeting'
            ? 'Schedule call'
            : composer_mode === 'message'
            ? 'Send message'
            : 'Log activity'
        }
        show_modal={
          composer_mode === 'note' || composer_mode === 'meeting' || composer_mode === 'message'
        }
        bodyClassName={styles.composerModal}
        onClose={onCloseComposer}
      >
        {composer_mode === 'meeting' ? (
          <div className={styles.meetingFields}>
            <div className={styles.fieldRow}>
              <input
                className={styles.input}
                placeholder="Invitee first name"
                value={meetingForm.firstname}
                onChange={(e) => setMeetingField('firstname', e.target.value)}
              />
              <input
                className={styles.input}
                placeholder="Invitee last name"
                value={meetingForm.lastname}
                onChange={(e) => setMeetingField('lastname', e.target.value)}
              />
            </div>
            <input
              className={styles.input}
              placeholder="Invitee email (required)"
              value={meetingForm.email}
              onChange={(e) => setMeetingField('email', e.target.value)}
            />
            <input
              className={styles.input}
              type="tel"
              placeholder="Invitee phone (optional)"
              value={meetingForm.phone}
              onChange={(e) => setMeetingField('phone', e.target.value)}
            />
            <input
              className={styles.input}
              type="datetime-local"
              value={meetingForm.time}
              onChange={(e) => setMeetingField('time', e.target.value)}
            />
            <input
              className={styles.input}
              placeholder="Title (optional)"
              value={meetingForm.title}
              onChange={(e) => setMeetingField('title', e.target.value)}
            />
            <input
              className={styles.input}
              placeholder="Google Meet link (optional)"
              value={meetingForm.link}
              onChange={(e) => setMeetingField('link', e.target.value)}
            />
            {effective_host.email ? (
              <span className={styles.hostHint}>Hosted by {personName(effective_host)}</span>
            ) : (
              <span className={styles.meetingWarn}>No host set — can&apos;t schedule.</span>
            )}
            <button
              type="button"
              className={styles.submit}
              onClick={handleScheduleMeeting}
              disabled={!can_schedule}
            >
              {saving ? 'Scheduling…' : 'Schedule call'}
            </button>
          </div>
        ) : composer_mode === 'message' ? (
          <div className={styles.meetingFields}>
            {contact_options.length === 0 && (
              <span className={styles.meetingWarn}>
                No contacts on file for this lead — can&apos;t send a message.
              </span>
            )}

            <MultiSelect
              options={message_options}
              value={message_options.find((o) => o.value === selected_message_id) || null}
              onChange={(option) => setSelectedMessageId(option?.value ?? null)}
              isMulti={false}
              isClearable
              placeholder="Search messages…"
              isLoading={messages_loading}
              menuPortalTarget={document.body}
            />

            <div className={styles.fieldRow}>
              <MultiSelect
                options={contact_options}
                value={contact_options.find((o) => o.value === selected_contact_id) || null}
                onChange={handleContactChange}
                isMulti={false}
                isClearable={false}
                placeholder="Select contact"
                disabled={contact_options.length === 0}
                menuPortalTarget={document.body}
              />
              <MultiSelect
                options={channel_options}
                value={channel_options.find((o) => o.value === channel) || null}
                onChange={(option) => setChannel(option?.value ?? null)}
                isMulti={false}
                isClearable={false}
                placeholder="Select channel"
                disabled={!selected_contact || channel_options.length === 0}
                menuPortalTarget={document.body}
              />
            </div>

            <button
              type="button"
              className={styles.submit}
              onClick={handleSendMessage}
              disabled={!can_send_message}
            >
              {sending_message ? 'Sending…' : 'Send'}
            </button>
          </div>
        ) : (
          <div className={styles.meetingFields}>
            <textarea
              className={styles.input}
              placeholder="Log a call or note…"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={3}
            />
            <div className={styles.row}>
              <div className={styles.segmented}>
                {OUTCOMES.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    className={`${styles.segmentedItem} ${
                      outcome === o.key ? styles.segActive : ''
                    }`}
                    onClick={() => setOutcome(o.key)}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className={styles.submit}
                onClick={handleLogActivity}
                disabled={saving || !body.trim()}
              >
                {saving ? 'Saving…' : 'Log'}
              </button>
            </div>
          </div>
        )}
      </GmModal>
    </div>
  );
};

export default SubjectTimeline;
